import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Types
export interface Run {
  id: string;
  name: string;
  timestamp: string;
  path?: string;
  info?: any;
  schema?: any;
  csv_files?: string[];
  input_csv_files?: string[];
  synthetic_csv_files?: string[];
  // Additional properties for DataWorkspacePage compatibility
  run_id?: string;
  has_schema?: boolean;
  has_synthetic_data?: boolean;
  has_validation?: boolean;
  has_input_data?: boolean;
  created_at?: string;
}

export interface AppState {
  runs: Run[];
  currentRun: Run | null;
  isLoading: boolean;
  error: string | null;
}

// Actions
type AppAction =
  | { type: 'SET_RUNS'; payload: Run[] }
  | { type: 'SET_CURRENT_RUN'; payload: Run | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

// Initial state
const initialState: AppState = {
  runs: [],
  currentRun: null,
  isLoading: false,
  error: null,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_RUNS':
      return { ...state, runs: action.payload };
    case 'SET_CURRENT_RUN':
      return { ...state, currentRun: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
}

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

